import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { constants, defaultOptions } from 'constants/constants';
import { SignatureComponent } from 'components/signature/signature.component';
import { DynamicFormComponent } from 'components/dynamic-form/dynamic-form.component';
import { OnInit } from '@angular/core';
import { DynamicFormContentDetails } from '../../dynamic-details/modal-content/dynamic-form-content-details/dynamic-form-content-details.component';
import { DynamicFormValue } from 'components/multiple-dynamic-form/multiple-dynamic-form.interface';
import { StepService } from 'services/professional/step/step.service';
import _flatten from 'lodash-es/flatten';
import _uniq from 'lodash-es/uniq';
import _get from 'lodash-es/get';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { map, switchMap } from 'rxjs/operators';
import { SignatureTestRegistrationInterface } from 'components/modal-windows/step-details/test-registration-details/test-registration-details.constants';
import { DynamicFormInfo } from 'interfaces/dynamic-form-info.interface';
import { isStepSubmitted } from 'utils/step.helpers';
import { DYNAMIC_FORM_ELEMENTS_PROVIDED_BY } from 'constants/workflow.constants';
import { TestRegistrationMixin } from 'common/mixins/test-registration/test-registration.mixin';

@UntilDestroy()
@Component({
  selector: 'cm-test-registration-form-content',
  templateUrl: './test-registration-form-content.component.html',
  styleUrls: ['./test-registration-form-content.component.scss'],
})
export class TestRegistrationFormContentComponent
  extends TestRegistrationMixin
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('stepSignature') stepSignatureComponent: SignatureComponent;
  @ViewChild('adaSignature') adaSignatureComponent: SignatureComponent;
  @ViewChild('testRegForm') testRegForm: DynamicFormComponent;
  @ViewChild('adaForm') adaForm: DynamicFormComponent;
  @Input() modalDetails: DynamicFormContentDetails;
  @Input() formInfo;
  @Input() signatureLink?: string;
  @Input() isStepSubmitted = false;
  @Input() isResetEnabled = false;
  @Input() step;
  @Output() formSubmit = new EventEmitter<any>();
  workflowTypeId: number;
  workflowId: number;
  phaseId: number;

  private examConfig: {
    fieldId?: number;
    valueMap: {
      [key: string]: number;
    };
  } = {
    valueMap: {},
  };

  dynamicForm: UntypedFormGroup;
  showErrors = false;
  dynamicFormErrors: string[] = [];
  FIELD_TYPES = constants.FORM_FIELD_TYPE_IDS;
  useAdaOption: boolean;
  readonly defaultOptions = defaultOptions;
  adaStep;
  adaFormInfo: DynamicFormInfo;

  constructor(private _modalService: NgbActiveModal, private cd: ChangeDetectorRef, private stepService: StepService) {
    super(stepService);
  }

  ngOnDestroy() {}

  updateFieldValue(event) {
    return super.updateFieldValue(event, {
      previewMode: this.isStepSubmitted,
      disabledByRoleIgnore: true,
    });
  }

  ngOnInit() {
    const fields = _get(this.step, 'content.forms[0].fields');
    if (fields) {
      this.step.content.forms[0].fields = this.validationMapElements(fields, 'filledByDisabled');
    }
    if (this.formInfo) {
      // TODO: check after solving the issue with Retrieve user data
      this.formInfo.fields = this.stepService.mapFormInfoFields(
        _get(this.step, 'content.forms[0].fields', []),
        this.formInfo,
      );
      // TODO This is temporary solution
      const { exams } = this.step.content;
      const examField = this.formInfo.fields.find(
        ({ providedBy, subType, type }) =>
          providedBy === DYNAMIC_FORM_ELEMENTS_PROVIDED_BY.TESTING_PROVIDER &&
          subType === constants.SELECT_SUB_TYPES.EXAM &&
          type.id === constants.FORM_FIELD_TYPE_IDS.SELECT,
      );
      this.examConfig = {
        fieldId: _get(examField, 'id'),
        valueMap: _get(examField, 'elementOptions', []).reduce((acc, { id, value }) => {
          const examId = _get(
            (exams || []).find(({ name }) => name === value),
            'id',
          );
          if (examId) {
            acc[id] = examId;
          }
          return acc;
        }, {}),
      };
    }
    if (this.step.content.useAda && !this.isStepSubmitted) {
      this.stepService
        .getCertificateStep(this.step.content.adaStepInstanceId)
        .pipe(
          untilDestroyed(this),
          switchMap((adaStep) =>
            this.stepService.getDynamicFormData(adaStep.step_id).pipe(map((adaFormInfo) => ({ adaStep, adaFormInfo }))),
          ),
        )
        .subscribe(({ adaStep, adaFormInfo }) => {
          this.adaStep = {
            ...adaStep,
            isStepSubmitted: isStepSubmitted(adaStep.status_id),
          };
          this.adaFormInfo = adaFormInfo;
          // TODO: check after solving the issue with Retrieve user data
          this.adaFormInfo.fields = this.stepService.mapFormInfoFields(
            _get(this.adaStep, 'content.forms[0].fields', []),
            this.adaFormInfo,
          );
        });
    }
    this.useAdaOption = false;
  }

  ngAfterViewInit() {
    if (this.isStepSubmitted && this.signatureLink) {
      this.stepSignatureComponent.signature = this.signatureLink;
    }
  }

  dismissModal() {
    this._modalService.dismiss();
  }

  submitForm() {
    const signatureErrorsMessage = [];
    const signature: SignatureTestRegistrationInterface = {};
    if (this.stepSignatureComponent) {
      const stepSignatureError = this.stepSignatureComponent.getSignatureErrors();

      if (!stepSignatureError) {
        signature.step = this.stepSignatureComponent.signature;
      } else {
        signatureErrorsMessage.push(stepSignatureError);
      }
    }
    if (this.adaSignatureComponent) {
      const adaSignatureError = this.adaSignatureComponent.getSignatureErrors();

      if (!adaSignatureError) {
        signature.ada = this.adaSignatureComponent.signature;
      } else {
        signatureErrorsMessage.push(adaSignatureError);
      }
    }

    const regForm = this.testRegForm.dynamicForm;
    let adaFormData = {};
    let adaForm = { valid: true } as any;
    if (this.step.content.useAda && this.useAdaOption) {
      adaForm = this.adaForm.dynamicForm;
      adaFormData = { formValue: adaForm.value };
    }

    const regFormData: DynamicFormValue = { formValue: regForm.value };
    const useAda = this.useAdaOption;
    const { valueMap, fieldId } = this.examConfig;
    const selectedExamId = valueMap[regFormData.formValue[fieldId]];
    if (regForm.valid && adaForm.valid && !signatureErrorsMessage.length) {
      this.dynamicFormErrors = [];
      this.showErrors = false;
      this.formSubmit.emit({ regFormData, adaFormData, useAda, signature, selectedExamId });
    } else {
      this.showErrors = true;
      const dynamicFormErrors = _flatten([
        this.testRegForm.getFormErrorMessages(),
        this.adaForm ? this.adaForm.getFormErrorMessages() : [],
      ]);
      this.dynamicFormErrors = _uniq(dynamicFormErrors);
      if (signatureErrorsMessage.length) {
        this.dynamicFormErrors.push(_uniq(signatureErrorsMessage) as any);
      }
    }
  }

  shouldApplyAda(useAdaOption: boolean) {
    if (useAdaOption) {
      this.useAdaOption = true;
    }
  }
}
